import {
  Badge,
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { shortOrderRef } from "@nexus/contracts";
import { useId, useRef, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";

import {
  describeDeadlineRollup,
  describeOrderAttention,
  describeStatusRollup,
  orderSelectionState,
  type OrdersDensity,
  type OrdersListOrder,
  type ScriptSelection,
} from "../../lib/orders-list-model";
import { ORDERS_OVERDUE_ROW_CLASS } from "../../lib/orders-overdue-row";
import { formatOrderLineCount } from "../../lib/order-line-count-label";
import {
  handoffBadgeVariant,
  isHandoffAlertStatus,
} from "../../lib/fulfillment-badge-variant";
import type { OrderScriptRow as OrderScriptRowData } from "../../types/marketplace.types";
import { OrderHandoffAlert } from "../OrderHandoffAlert";
import { OrderDeadlineRollup } from "./OrderDeadlineRollup";
import {
  ORDERS_CELL_SCRIPTS_CLASS,
  ORDERS_CELL_STATUS_CLASS,
  ORDERS_CELL_SENT_CLASS,
  ORDERS_CELL_DUE_CLASS,
  ORDERS_CELL_TYPE_CLASS,
  ORDERS_CHECKBOX_HIT_TARGET_CLASS,
  ORDERS_GRID_CLASS,
  ORDERS_ROW_PADDING_CLASS,
} from "./orders-list-grid";
import { OrderScriptRow } from "./OrderScriptRow";
import { OrderTypeBadge } from "./OrderTypeBadge";
import { CopyableId } from "./CopyableId";
import { OrderStatusRollup } from "./OrderStatusRollup";
import { VendorBriefActions } from "./vendor-brief/VendorBriefActions";
import { VendorIdentity } from "./VendorIdentity";
import {
  formatMarketplaceSentAt,
  formatMarketplaceSentColumn,
} from "../../lib/format-marketplace-timestamp";

export interface OrderRowProps {
  chrome?: "marketplace" | "hub";
  order: OrdersListOrder;
  tenantId: string;
  variant: "in-production" | "history" | "archive";
  todayYmd: string;
  canEdit: boolean;
  expanded: boolean;
  /** True while any work panel is open — Left/Right then move scripts, not expand. */
  panelOpen: boolean;
  /** Order panel is open for this row. */
  panelSelected: boolean;
  /** Script currently shown in the work panel, if any. */
  panelScriptId: string | null;
  /** Roving tabindex: only the active row is in the tab order. */
  tabIndex: 0 | -1;
  rowIndex: number;
  activeRowId: string | null;
  density: OrdersDensity;
  selection: ScriptSelection;
  pendingScriptIds: ReadonlySet<string>;
  retryPending: boolean;
  onToggleExpanded: () => void;
  onFocusTreeRow: (rowId: string) => void;
  onToggleOrderSelected: () => void;
  onToggleScriptSelected: (scriptId: string) => void;
  onScriptStatusChange: (
    scriptId: string,
    next: OrderScriptRowData["fulfillmentStatus"],
  ) => void;
  onRetryHandoff: () => void;
  onViewHistory: () => void;
  onOpenOrderDetails: () => void;
  onOpenScriptDetails: (script: OrderScriptRowData) => void;
  onDownloadAsset: (script: OrderScriptRowData) => Promise<void>;
}

function formatCreatedAt(iso: string, locale: string): string {
  return formatMarketplaceSentAt(iso, locale);
}

/** Row stamp: time is the extra fact vs the Sent column; drop the year when it's this year. */
function formatCreatedStamp(
  iso: string,
  locale: string,
  now = new Date(),
): string {
  const date = new Date(iso);
  const sameYear = date.getFullYear() === now.getFullYear();
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
    timeZoneName: "short",
  }).format(date);
}

export function OrderRow({
  chrome = "marketplace",
  order,
  tenantId,
  variant,
  todayYmd,
  canEdit,
  expanded,
  panelOpen,
  panelSelected,
  panelScriptId,
  tabIndex,
  rowIndex,
  activeRowId,
  density,
  selection,
  pendingScriptIds,
  retryPending,
  onToggleExpanded,
  onFocusTreeRow,
  onToggleOrderSelected,
  onToggleScriptSelected,
  onScriptStatusChange,
  onRetryHandoff,
  onViewHistory,
  onOpenOrderDetails,
  onOpenScriptDetails,
  onDownloadAsset,
}: OrderRowProps) {
  const { t, i18n } = useTranslation();
  const rowRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const { copy: copyLink } = useCopyToClipboard({
    onCopy: () => toast.success(t("marketplace.orders.list.panel.linkCopied")),
  });

  const statusRollup = describeStatusRollup(order);
  const deadlineRollup = describeDeadlineRollup(order, todayYmd);
  const isOverdue = describeOrderAttention(order, todayYmd).overdue;
  const selectionState = orderSelectionState(order, selection);
  const handoffAlert = isHandoffAlertStatus(order.status) ? order.status : null;
  const sentIso = order.sentAt ?? order.createdAt;
  const handleCopyLink = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("tab", variant);
    url.searchParams.set("order", order.id);
    url.searchParams.delete("script");
    void copyLink(url.toString());
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    switch (event.key) {
      case "Enter":
        event.preventDefault();
        if (!expanded) onToggleExpanded();
        onOpenOrderDetails();
        break;
      case " ":
        event.preventDefault();
        if (canEdit) onToggleOrderSelected();
        break;
      case "ArrowRight":
        if (panelOpen) break;
        if (!expanded) {
          event.preventDefault();
          onToggleExpanded();
        }
        break;
      case "ArrowLeft":
        if (panelOpen) break;
        if (expanded) {
          event.preventDefault();
          onToggleExpanded();
        }
        break;
    }
  };

  return (
    <li
      role="presentation"
      className="relative transition-shadow duration-200 ease-out motion-reduce:transition-none"
    >
      <div
        ref={rowRef}
        role="row"
        aria-level={1}
        aria-rowindex={rowIndex}
        data-order-row
        data-orders-row-chrome={chrome}
        data-order-id={order.id}
        data-overdue={isOverdue ? "true" : undefined}
        data-tree-row
        data-tree-row-id={`order:${order.id}`}
        tabIndex={tabIndex}
        aria-expanded={expanded}
        aria-controls={panelId}
        aria-label={t("marketplace.orders.list.rowLabel", {
          vendor: order.vendor.name,
          count: order.scripts.length,
        })}
        onFocus={() => onFocusTreeRow(`order:${order.id}`)}
        onClick={() => {
          if (!expanded) onToggleExpanded();
          onOpenOrderDetails();
        }}
        onKeyDown={handleKeyDown}
        className={cn(
          ORDERS_GRID_CLASS,
          ORDERS_ROW_PADDING_CLASS,
          "group/row scroll-mt-20 cursor-pointer text-left outline-none transition-colors duration-150 ease-out",
          density === "compact" ? "min-h-13 py-1.5" : "min-h-16 py-2.5",
          "focus-visible:bg-muted/60 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/50",
          selectionState !== "none"
            ? "bg-primary-subtle/70"
            : panelSelected
              ? "bg-primary-subtle/40"
              : chrome === "hub"
                ? "bg-muted/60 hover:bg-muted"
                : "bg-card hover:bg-muted/30",
          expanded &&
            selectionState === "none" &&
            !panelSelected &&
            (chrome === "hub" ? "bg-muted font-medium" : "bg-card"),
          isOverdue && ORDERS_OVERDUE_ROW_CLASS,
        )}
      >
        <span role="gridcell" className="flex items-center">
          {canEdit ? (
            <Checkbox
              checked={
                selectionState === "all"
                  ? true
                  : selectionState === "some"
                    ? "indeterminate"
                    : false
              }
              aria-label={t("marketplace.orders.list.selectOrder", {
                vendor: order.vendor.name,
              })}
              onCheckedChange={onToggleOrderSelected}
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
              className={cn(
                ORDERS_CHECKBOX_HIT_TARGET_CLASS,
                "data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground",
              )}
            />
          ) : null}
        </span>

        <span role="gridcell" className="flex items-center">
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={t(
              expanded
                ? "marketplace.orders.list.collapseOrder"
                : "marketplace.orders.list.expandOrder",
              { vendor: order.vendor.name },
            )}
            className={cn(
              "flex cursor-pointer items-center justify-center rounded-sm text-muted-foreground transition-opacity hover:bg-background hover:text-foreground focus-visible:opacity-100",
              chrome === "hub"
                ? "ml-auto size-6 opacity-0 group-hover/row:opacity-100"
                : "size-7 -ml-1.5 text-muted-foreground/70 transition-colors hover:bg-muted",
              chrome === "hub" && expanded && "opacity-100",
            )}
            onClick={(event) => {
              event.stopPropagation();
              onToggleExpanded();
            }}
          >
            <Icon
              name="chevron-right"
              size="sm"
              color="inherit"
              className={cn(
                "transition-transform duration-200 ease-out motion-reduce:transition-none",
                expanded && "rotate-90",
              )}
              aria-hidden
            />
          </button>
        </span>

        <div role="gridcell" className="relative flex min-w-0 items-center gap-2.5">
          <VendorIdentity
            name={order.vendor.name}
            logoUrl={order.vendor.logoUrl}
          />
          <span className="min-w-0">
            <span className="flex min-w-0 items-center gap-2">
              <span
                className={cn(
                  "min-w-0 truncate text-sm text-foreground",
                  chrome === "hub" ? "font-medium" : "font-semibold",
                )}
              >
                {order.vendor.name}
              </span>
              {handoffAlert ? (
                <Badge
                  variant={handoffBadgeVariant(handoffAlert, "client")}
                  dot
                  className="shrink-0"
                >
                  {t(`marketplace.orders.status.${handoffAlert}`)}
                </Badge>
              ) : null}
            </span>
            <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-caption text-muted-foreground">
              <CopyableId
                value={order.id}
                displayValue={shortOrderRef(order.id) ?? order.id}
                density="quiet"
                copyLabel={t("marketplace.orders.copyOrderId")}
                copiedMessage={t("marketplace.orders.copyOrderIdSuccess")}
                className="min-w-0"
              />
              <span aria-hidden className="shrink-0 text-muted-foreground/40">
                ·
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <time
                    dateTime={order.createdAt}
                    className="shrink-0 tabular-nums"
                  >
                    {formatCreatedStamp(order.createdAt, i18n.language)}
                  </time>
                </TooltipTrigger>
                <TooltipContent>
                  {t("marketplace.orders.list.created", {
                    date: formatCreatedAt(order.createdAt, i18n.language),
                  })}
                </TooltipContent>
              </Tooltip>
            </span>
            <span className="mt-1 flex flex-wrap items-center gap-2 sm:hidden">
              <OrderStatusRollup rollup={statusRollup} />
              <OrderDeadlineRollup
                rollup={deadlineRollup}
                todayYmd={todayYmd}
                createdAt={order.createdAt}
              />
            </span>
          </span>
          {chrome === "hub" ? (
            <Button
              type="button"
              variant="secondary"
              size="xs"
              iconStart="eye"
              className="absolute right-2 bottom-1.5 bg-muted opacity-0 shadow-sm group-hover/row:opacity-100 focus-visible:opacity-100"
              aria-label={t(
                "personaOpportunities.hub.opportunities.viewAria",
                {
                  title: order.vendor.name,
                  defaultValue: `View ${order.vendor.name}`,
                },
              )}
              onClick={(event) => {
                event.stopPropagation();
                onOpenOrderDetails();
              }}
            >
              {t("personaOpportunities.hub.opportunities.view", "View")}
            </Button>
          ) : null}
        </div>

        <span role="gridcell" className={ORDERS_CELL_TYPE_CLASS}>
          <OrderTypeBadge type={order.orderType} />
        </span>

        <span
          role="gridcell"
          className={cn(
            ORDERS_CELL_SCRIPTS_CLASS,
            "text-xs tabular-nums text-muted-foreground",
          )}
        >
          {formatOrderLineCount(order.scripts, t)}
        </span>

        <span role="gridcell" className={ORDERS_CELL_STATUS_CLASS}>
          <OrderStatusRollup rollup={statusRollup} />
        </span>

        <Tooltip>
          <TooltipTrigger asChild>
            <time
              role="gridcell"
              dateTime={sentIso}
              className={cn(
                ORDERS_CELL_SENT_CLASS,
                "text-xs tabular-nums text-muted-foreground",
              )}
            >
              {formatMarketplaceSentColumn(sentIso, i18n.language)}
            </time>
          </TooltipTrigger>
          <TooltipContent>
            {t("marketplace.orders.list.sentTooltip", {
              date: formatCreatedAt(sentIso, i18n.language),
            })}
          </TooltipContent>
        </Tooltip>

        <span role="gridcell" className={ORDERS_CELL_DUE_CLASS}>
          <OrderDeadlineRollup
            rollup={deadlineRollup}
            todayYmd={todayYmd}
            createdAt={order.createdAt}
          />
        </span>

        <span role="gridcell" className="flex items-center justify-end">
          <VendorBriefActions
            snapshot={order.handoff}
            orderId={order.id}
            tenantId={tenantId}
            tenantVendorId={order.tenantVendorId}
            sentByUserName={order.sentByUserName?.trim() || null}
            viewer="client"
          >
            {(briefItems) => (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t("marketplace.orders.list.moreActions", {
                      vendor: order.vendor.name,
                    })}
                    className="text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover/row:opacity-100 group-focus-within/row:opacity-100 data-[state=open]:opacity-100"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <Icon name="more-horizontal" size="sm" color="inherit" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="min-w-44"
                  onClick={(event) => event.stopPropagation()}
                >
                  {briefItems}
                  {briefItems ? <DropdownMenuSeparator /> : null}
                  {variant === "in-production" ? (
                    <DropdownMenuItem
                      icon={
                        order.fulfillmentStatus === "CANCELLED"
                          ? "file"
                          : "history"
                      }
                      className="text-xs"
                      onSelect={onViewHistory}
                    >
                      {t(
                        order.fulfillmentStatus === "CANCELLED"
                          ? "marketplace.orders.list.panel.viewArchive"
                          : "marketplace.orders.list.panel.viewHistory",
                        order.fulfillmentStatus === "CANCELLED"
                          ? "View in Archive"
                          : "View in History",
                      )}
                    </DropdownMenuItem>
                  ) : null}
                  <DropdownMenuItem
                    icon="link"
                    className="text-xs"
                    onSelect={handleCopyLink}
                  >
                    {t("marketplace.orders.list.panel.copyLink")}
                  </DropdownMenuItem>
                  {handoffAlert ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        icon="refresh-cw"
                        className="text-xs"
                        disabled={retryPending}
                        onSelect={onRetryHandoff}
                      >
                        {retryPending
                          ? t("marketplace.orders.handoff.retrying")
                          : t("marketplace.orders.handoff.retry")}
                      </DropdownMenuItem>
                    </>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </VendorBriefActions>
        </span>
      </div>

      <div
        id={panelId}
        className={cn(
          "grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none",
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="min-h-0 overflow-hidden">
          {expanded ? (
            <div className="border-t border-border bg-muted/20">
              {handoffAlert ? (
                <div
                  className={cn(ORDERS_ROW_PADDING_CLASS, "pt-2 pl-[5.75rem]")}
                >
                  <OrderHandoffAlert
                    order={order}
                    retryPending={retryPending}
                    onRetry={onRetryHandoff}
                  />
                </div>
              ) : null}

              <ul
                role="presentation"
                className="flex flex-col divide-y divide-border-subtle/70"
              >
                {order.scripts.map((script, index) => (
                  <OrderScriptRow
                    key={script.id}
                    order={order}
                    script={script}
                    index={index + 1}
                    todayYmd={todayYmd}
                    canEdit={canEdit}
                    selected={selection.has(script.id)}
                    pending={pendingScriptIds.has(script.id)}
                    uniformDeadline={deadlineRollup.isUniform}
                    density={density}
                    chrome={chrome}
                    tabIndex={activeRowId === `script:${script.id}` ? 0 : -1}
                    rowIndex={rowIndex + index + 1}
                    onToggleSelected={() => onToggleScriptSelected(script.id)}
                    onFocusRow={() => onFocusTreeRow(`script:${script.id}`)}
                    highlighted={panelScriptId === script.id}
                    onOpenDetails={() => onOpenScriptDetails(script)}
                    onStatusChange={(next) =>
                      onScriptStatusChange(script.id, next)
                    }
                    onDownloadAsset={() => onDownloadAsset(script)}
                  />
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    </li>
  );
}
